import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceDot } from "recharts";

import { API } from "../config";
import PortfolioTrendStorico from "./PortfolioTrendStorico";
import IconaInfoModal from "../components/IconaInfoModal";

// ===== STILI BASE =====
const btnBase = {
  fontFamily:"'DM Sans',sans-serif",fontSize:12,fontWeight:600,
  border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",
  display:"inline-flex",alignItems:"center",gap:6,
  transition:"opacity 0.15s",
};
const btnPrimary = {...btnBase,background:"#2563EB",color:"#fff"};
const btnSave    = {...btnBase,background:"#16A34A",color:"#fff"};
const btnDanger  = {...btnBase,background:"#DC2626",color:"#fff"};
const btnGhost   = {...btnBase,background:"transparent",color:"#6B6B66",border:"1px solid #E8E8E4"};
const inputStyle = {
  width:"100%",border:"1px solid #E8E8E4",borderRadius:6,padding:"8px 10px",
  fontSize:13,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box",
};

// ===== POPOVER ESPLICATIVI KPI ESPOSIZIONE (v185) =====
const INFO_ESP_MEDIA_STRATEGIA = (
  <IconaInfoModal titolo="Esposizione Media">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Media</b> è il rischio teorico medio per evento della strategia. Misura "quanto rischi in media, ogni volta che si entra a mercato".
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Calcolo del rischio teorico per singolo evento:</b></p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Ingresso in Punta: rischio = <b>1</b> (capitale impegnato)</li>
      <li style={{marginBottom:4}}>Ingresso in Banca: rischio = <b>quota − 1</b> (capitale potenzialmente perso se l'evento si verifica)</li>
    </ul>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Esposizione Media</b>: somma del rischio teorico di tutti gli eventi ÷ numero di eventi.
    </p>
    <p style={{margin:"0 0 12px 0"}}>
      Una strategia aggrega uno o più sistemi.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio</b> (strategia con 2 sistemi, A e B):</p>
    <ul style={{margin:"0 0 0 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Sistema A su 1 partita in Punta → 1 evento, rischio 1.00</li>
      <li style={{marginBottom:4}}>Sistema B su 2 partite in Banca quota 3.00 → 2 eventi, rischio 2 × 2.00 = 4.00</li>
      <li style={{marginBottom:4}}>Totale 3 eventi, rischio cumulato 5.00</li>
      <li style={{marginBottom:4}}><b>Esposizione Media = 5.00 / 3 ≈ 1.67</b></li>
    </ul>
  </IconaInfoModal>
);

const INFO_ESP_GIORN_STRATEGIA = (
  <IconaInfoModal titolo="Esposizione Max Giornaliera">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Max Giornaliera</b> è il massimo rischio teorico accumulato in una singola giornata nella storia della strategia.
    </p>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Calcolo</b>: per ogni giornata con almeno un evento, somma il rischio teorico di tutti gli eventi della giornata (su tutti i sistemi della strategia). L'Esposizione Max Giornaliera è il massimo storico tra queste somme.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio</b> (sabato con strategia composta da 2 sistemi):</p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Sistema A: 1 evento Punta (1.00) + 1 evento Banca quota 3.00 (2.00) = 3.00</li>
      <li style={{marginBottom:4}}>Sistema B: 1 evento Banca quota 2.50 (1.50) sulla stessa giornata = 1.50</li>
      <li style={{marginBottom:4}}>Totale sabato = 4.50</li>
    </ul>
    <p style={{margin:0}}>
      Se questo è il giorno più carico della storia della strategia, <b>Esposizione Max Giornaliera = 4.50</b>.
    </p>
  </IconaInfoModal>
);

const INFO_ESP_SETT_STRATEGIA = (
  <IconaInfoModal titolo="Esposizione Max Settimanale">
    <p style={{margin:"0 0 12px 0"}}>
      L'<b>Esposizione Max Settimanale</b> è il massimo rischio teorico accumulato in un singolo ciclo operativo nella storia della strategia.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Cicli operativi della piattaforma:</b></p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}><b>Weekend</b>: da venerdì a lunedì compresi</li>
      <li style={{marginBottom:4}}><b>Midweek</b>: da martedì a giovedì compresi</li>
    </ul>
    <p style={{margin:"0 0 12px 0"}}>
      <b>Calcolo</b>: per ogni ciclo storico con almeno un evento, somma il rischio teorico di tutti gli eventi del ciclo (su tutti i sistemi della strategia). L'Esposizione Max Settimanale è il massimo storico tra queste somme.
    </p>
    <p style={{margin:"0 0 8px 0"}}><b>Esempio</b> (ciclo Weekend 13-16 marzo, strategia con 2 sistemi):</p>
    <ul style={{margin:"0 0 12px 0",paddingLeft:20,listStyleType:"disc"}}>
      <li style={{marginBottom:4}}>Sistema A: ven 2.00 + sab 3.00 + dom 1.00 = 6.00</li>
      <li style={{marginBottom:4}}>Sistema B: ven 1.50 + lun 2.00 = 3.50</li>
      <li style={{marginBottom:4}}>Totale ciclo = 9.50</li>
    </ul>
    <p style={{margin:0}}>
      Se questo è il ciclo più carico storico, <b>Esposizione Max Settimanale = 9.50</b>.
    </p>
  </IconaInfoModal>
);

// ===== HELPER FORMATO KPI (replica da Portfolio.js v176) =====
function fmtPunti(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  return (n >= 0 ? "+" : "") + n.toFixed(2);
}
// Max P/L visualizzato senza segno + (D487 v174)
function fmtNoSegno(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(2);
}
function colPunti(v) {
  if (v > 0) return "#16A34A";
  if (v < 0) return "#DC2626";
  return "#64748B";
}
function fmtNum(v, dec=2) {
  if (v === null || v === undefined) return "—";
  return Number(v).toFixed(dec);
}
// Quota Media — N/A in italic grigio per strategie (D473 v173 + D495 v175)
function fmtQuotaStrategia() {
  return <span style={{fontStyle:"italic",color:"#94A3B8"}}>N/A</span>;
}

// ===== BADGE STATO (femminile: strategia "Attiva"/"Non attiva" — D478 lezione v178) =====
function BadgeStato({ attivo }) {
  if (attivo) {
    return (
      <span style={{
        fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:12,
        background:"#DCFCE7",color:"#15803D",letterSpacing:"0.04em",
        textTransform:"uppercase",whiteSpace:"nowrap",
      }}>Attiva</span>
    );
  }
  return (
    <span style={{
      fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:12,
      background:"#EDE9FE",color:"#7C3AED",letterSpacing:"0.04em",
      textTransform:"uppercase",whiteSpace:"nowrap",
    }}>Non attiva</span>
  );
}

// ===== ICONA SORT =====
function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <span style={{color:"#CBD5E1",marginLeft:4}}>↕</span>;
  return <span style={{color:"#2563EB",marginLeft:4}}>{sortDir === "asc" ? "↑" : "↓"}</span>;
}

// ===== HELPER GRAFICI EQUITY / DRAWDOWN =====

function _fmtDataIT(s) {
  if (!s || typeof s !== "string") return s || "";
  const p = s.split("-");
  if (p.length < 3) return s;
  return `${p[2]}/${p[1]}/${p[0]}`;
}

function _calcolaCambiStagione(equity) {
  if (!Array.isArray(equity) || equity.length === 0) return [];
  const cambi = [];
  let prev = equity[0]?.stagione;
  for (let i = 1; i < equity.length; i++) {
    const s = equity[i]?.stagione;
    if (s && s !== prev) {
      cambi.push({ idx: i, stagione: s, data: equity[i].data });
      prev = s;
    }
  }
  return cambi;
}

function _makeTooltipEquity(totale) {
  return function TooltipEquity({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
        boxShadow:"0 4px 12px rgba(0,0,0,0.08)",padding:"8px 10px",
        fontFamily:"'DM Sans',sans-serif",fontSize:11}}>
        <div style={{color:"#1E293B",fontWeight:600,marginBottom:2}}>
          {_fmtDataIT(p.data)} {p.stagione ? `· stag. ${p.stagione}` : ""}
        </div>
        <div style={{color:"#64748B",marginBottom:4}}>
          Evento {p.n} di {totale}
        </div>
        <div style={{color: p.punti_prog >= 0 ? "#15803D" : "#DC2626",fontWeight:700}}>
          {p.punti_prog >= 0 ? "+" : ""}{Number(p.punti_prog).toFixed(2)} pt
        </div>
      </div>
    );
  };
}

function _makeTooltipDrawdown(totale) {
  return function TooltipDrawdown({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    const dd = Number(p.drawdown) || 0;
    return (
      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
        boxShadow:"0 4px 12px rgba(0,0,0,0.08)",padding:"8px 10px",
        fontFamily:"'DM Sans',sans-serif",fontSize:11}}>
        <div style={{color:"#1E293B",fontWeight:600,marginBottom:2}}>
          {_fmtDataIT(p.data)} {p.stagione ? `· stag. ${p.stagione}` : ""}
        </div>
        <div style={{color:"#64748B",marginBottom:4}}>
          Evento {p.n} di {totale}
        </div>
        <div style={{color: dd > 0 ? "#DC2626" : "#94A3B8",fontWeight:700}}>
          {dd > 0 ? `-${dd.toFixed(2)}` : "0"} pt
        </div>
      </div>
    );
  };
}

// ===== TABELLA STRATEGIE — lista master Portfolio Strategie (refactor v179, D491-D496) =====
function TabellaStrategie({ dati, onClickRiga, stagioniFiltro, onApriModifica, onElimina }) {
  const [sortCol, setSortCol] = useState("punti");
  const [sortDir, setSortDir] = useState("desc");
  const [menuApertoIdx, setMenuApertoIdx] = useState(null);
  const [menuPos, setMenuPos] = useState({top:0, right:0});
  const menuRef = useRef(null);

  // Click-outside menù •••
  useEffect(() => {
    const handler = (e) => {
      if (menuApertoIdx !== null && menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuApertoIdx(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuApertoIdx]);

  // Chiusura menù su scroll (con position:fixed il dropdown resta sospeso, va chiuso)
  useEffect(() => {
    if (menuApertoIdx === null) return;
    const handler = () => setMenuApertoIdx(null);
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [menuApertoIdx]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  // KPI per riga con filtro stagione (replica getRow di TabellaSistemi)
  const getRow = (s) => {
    const sel = stagioniFiltro && stagioniFiltro.size > 0;
    if (!sel) {
      return {
        eventi: s.eventi, punti: s.punti, ppe: s.ppe,
        pct_vincenti: s.pct_vincenti,
        max_lose_run: s.max_lose_run, max_win_run: s.max_win_run,
        max_drawdown: s.max_drawdown,
        max_pl: s.max_pl,
        espo_max_giornaliera: s.espo_max_giornaliera,
        espo_max_settimanale: s.espo_max_settimanale,
        esp_media: s.esp_media,
      };
    }
    const acc = Object.entries(s.dati_per_stagione || {})
      .filter(([st]) => stagioniFiltro.has(st))
      .reduce((a, [, v]) => ({
        eventi: (a.eventi||0) + v.eventi,
        punti: (a.punti||0) + v.punti,
        vincenti: (a.vincenti||0) + Math.round(v.pct_vincenti/100*v.eventi),
        max_lose_run: Math.max(a.max_lose_run||0, v.max_lose_run||0),
        max_win_run: Math.max(a.max_win_run||0, v.max_win_run||0),
        max_drawdown: Math.max(a.max_drawdown||0, v.max_drawdown||0),
        esp_sum: (a.esp_sum||0) + (v.esp_media != null ? v.esp_media * v.eventi : 0),
        esp_ev:  (a.esp_ev||0)  + (v.esp_media != null ? v.eventi : 0),
        espo_gio_max: Math.max(a.espo_gio_max||0, v.espo_max_giornaliera||0),
        espo_set_max: Math.max(a.espo_set_max||0, v.espo_max_settimanale||0),
      }), {});
    const ev = acc.eventi || 0;
    const pt = Math.round((acc.punti||0) * 100) / 100;
    return {
      eventi: ev,
      punti: pt,
      ppe: ev > 0 ? Math.round(pt/ev*10000)/10000 : 0,
      pct_vincenti: ev > 0 ? Math.round((acc.vincenti||0)/ev*10000)/100 : 0,
      max_lose_run: acc.max_lose_run||0,
      max_win_run: acc.max_win_run||0,
      max_drawdown: acc.max_drawdown||0,
      // Max P/L con filtro stagione: approssimazione = max(0, sum punti filtrati)
      max_pl: Math.max(0, pt),
      // Esposizioni: max per Giorn./Sett., media ponderata per Esp. Media
      espo_max_giornaliera: acc.espo_gio_max || 0,
      espo_max_settimanale: acc.espo_set_max || 0,
      esp_media: acc.esp_ev > 0 ? Math.round(acc.esp_sum / acc.esp_ev * 10000) / 10000 : 0,
    };
  };

  const sorted = [...dati].sort((a, b) => {
    let va, vb;
    if (sortCol === "nome") {
      va = a.nome ?? ""; vb = b.nome ?? "";
    } else if (sortCol === "attivo") {
      va = a.attivo ? 1 : 0; vb = b.attivo ? 1 : 0;
    } else {
      const ra = getRow(a); const rb = getRow(b);
      va = ra[sortCol] ?? 0; vb = rb[sortCol] ?? 0;
    }
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const colNumerica = (col) => ["eventi","punti","ppe","max_pl","quota_media","esp_media","espo_max_giornaliera","espo_max_settimanale","pct_vincenti","max_win_run","max_lose_run","max_drawdown"].includes(col);

  const thStyle = (col, sortable=true) => ({
    padding:"10px 12px", fontSize:11, fontWeight:700, color:"#64748B",
    textTransform:"uppercase", letterSpacing:"0.05em",
    textAlign: colNumerica(col) ? "right" : (col === "attivo" ? "center" : "left"),
    cursor: sortable ? "pointer" : "default", userSelect:"none", whiteSpace:"nowrap",
    background:"#F8FAFC", borderBottom:"2px solid #E8E8E4",
  });
  const tdStyle = (align="left") => ({
    padding:"10px 12px", fontSize:13, color:"#1E293B",
    borderBottom:"1px solid #F1F5F9", textAlign:align,
    verticalAlign:"middle",
  });

  if (!dati.length) return (
    <div style={{padding:40,textAlign:"center",color:"#94A3B8",fontSize:13}}>
      Nessuna strategia corrisponde ai filtri attivi.
    </div>
  );

  const COL_VIOLA = "#7C3AED";

  return (
    <div style={{overflowX:"auto",overflowY:"visible"}}>
      <table style={{width:"100%",borderCollapse:"collapse",minWidth:1200}}>
        <thead>
          <tr>
            {[
              {col:"nome",                 label:"Nome"},
              {col:"attivo",               label:"Stato"},
              {col:"eventi",               label:"Eventi"},
              {col:"punti",                label:"Punti"},
              {col:"ppe",                  label:"PPE"},
              {col:"max_pl",               label:"Max P/L"},
              {col:"quota_media",          label:"Quota Media", sortable:false, tooltip:"N/A per le strategie (aggregano mercati eterogenei)"},
              {col:"esp_media",            label:"Esp. Media", tooltip:"Esposizione Media per evento (rischio teorico medio)"},
              {col:"espo_max_giornaliera", label:"Esp. Max Giorn.", tooltip:"Esposizione Max Giornaliera"},
              {col:"espo_max_settimanale", label:"Esp. Max Sett.", tooltip:"Esposizione Max Settimanale (ciclo operativo mar-gio/ven-lun)"},
              {col:"pct_vincenti",         label:"Win Rate"},
              {col:"max_win_run",          label:"Max Win"},
              {col:"max_lose_run",         label:"Max Lose"},
              {col:"max_drawdown",         label:"Max DD",      tooltip:"Massimo Drawdown"},
            ].map(({col,label,tooltip,sortable=true}) => (
              <th key={col}
                  style={thStyle(col, sortable)}
                  onClick={sortable ? () => handleSort(col) : undefined}
                  title={tooltip || ""}>
                {label}{sortable && <SortIcon col={col} sortCol={sortCol} sortDir={sortDir}/>}
              </th>
            ))}
            {/* Coda menù ••• (no header testuale) */}
            <th style={{...thStyle("menu", false), width:40}}></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((s, idx) => {
            const r = getRow(s);
            const nSistemi = (s.sistemi_nomi || s.sistemi_ids || []).length;
            const nomiSistemi = (s.sistemi_nomi || []).join(", ");
            const emailsRiga = (s.indirizzi_mail || "").split(",").map(m => m.trim()).filter(Boolean);
            const nomiRigaArr = (s.nomi_destinatari || "").split(",").map(n => n.trim());
            const nDestinatari = emailsRiga.length;
            const tooltipDestinatari = nDestinatari > 0
              ? emailsRiga.map((_, i) => (nomiRigaArr[i] || "(senza nome)")).join("\n")
              : "";
            const sottotitolo = nSistemi > 0
              ? `${nSistemi} sistem${nSistemi===1?"a":"i"}`
              : "—";
            return (
              <tr key={s.id}
                onClick={() => onClickRiga && onClickRiga(s)}
                style={{cursor:"pointer", transition:"background 0.1s"}}
                onMouseEnter={e => e.currentTarget.style.background="#F0F7FF"}
                onMouseLeave={e => e.currentTarget.style.background=""}
              >
                {/* 1. Nome (con sottotitolo, icona 💬 commenti, icona 👤 destinatari) */}
                <td style={tdStyle()}>
                  <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                    <span style={{fontWeight:600,color:"#1E293B"}}>{s.nome}</span>
                    {s.commenti && (
                      <span title={s.commenti} style={{
                        fontSize:14,color:"#94A3B8",cursor:"help",lineHeight:1,
                      }}>💬</span>
                    )}
                    {nDestinatari > 0 && (
                      <span title={tooltipDestinatari}
                        style={{
                          fontSize:11,color:"#64748B",cursor:"help",lineHeight:1,
                          display:"inline-flex",alignItems:"center",gap:2,
                          whiteSpace:"pre-line",
                        }}>👤 {nDestinatari}</span>
                    )}
                  </div>
                  <div title={nomiSistemi || ""}
                    style={{fontSize:11,color:"#94A3B8",marginTop:2,fontFamily:"'IBM Plex Mono',monospace",
                      cursor: nomiSistemi ? "help" : "default"}}>
                    {sottotitolo}
                  </div>
                </td>
                {/* 2. Stato */}
                <td style={tdStyle("center")}>
                  <BadgeStato attivo={s.attivo !== false} />
                </td>
                {/* 3. Eventi */}
                <td style={tdStyle("right")}>{(r.eventi||0).toLocaleString("it-IT")}</td>
                {/* 4. Punti (verde/rosso) */}
                <td style={{...tdStyle("right"),fontWeight:700,color:colPunti(r.punti),fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtPunti(r.punti)}
                </td>
                {/* 5. PPE */}
                <td style={{...tdStyle("right"),fontFamily:"'IBM Plex Mono',monospace",color:colPunti(r.ppe),fontWeight:600}}>
                  {fmtPunti(r.ppe)}
                </td>
                {/* 6. Max P/L (verde, senza segno +) */}
                <td style={{...tdStyle("right"),color:"#16A34A",fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNoSegno(r.max_pl)}
                </td>
                {/* 7. Quota Media — N/A per strategie (D495) */}
                <td style={tdStyle("right")}>{fmtQuotaStrategia()}</td>
                {/* 8. Esp. Media (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.esp_media, 2)}
                </td>
                {/* 9. Esp. Max Giorn. (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.espo_max_giornaliera)}
                </td>
                {/* 10. Esp. Max Sett. (viola) */}
                <td style={{...tdStyle("right"),color:COL_VIOLA,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace"}}>
                  {fmtNum(r.espo_max_settimanale)}
                </td>
                {/* 9. Win Rate */}
                <td style={tdStyle("right")}>{fmtNum(r.pct_vincenti, 1)}%</td>
                {/* 10. Max Win Run (verde) */}
                <td style={{...tdStyle("right"),color:"#16A34A",fontWeight:600}}>{r.max_win_run}</td>
                {/* 11. Max Lose Run (rosso) */}
                <td style={{...tdStyle("right"),color:"#DC2626",fontWeight:600}}>{r.max_lose_run}</td>
                {/* 12. Max Drawdown (rosso) */}
                <td style={{...tdStyle("right"),color:"#DC2626",fontFamily:"'IBM Plex Mono',monospace"}}>{fmtNum(r.max_drawdown)}</td>
                {/* Coda menù ••• (D496) — dropdown con position:fixed per scavalcare overflow:hidden del container (fix v179.2) */}
                <td style={{...tdStyle("center"),width:56,paddingRight:16}}
                  onClick={e => e.stopPropagation()}>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (menuApertoIdx !== idx) {
                        const r = e.currentTarget.getBoundingClientRect();
                        setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                      }
                      setMenuApertoIdx(menuApertoIdx === idx ? null : idx);
                    }}
                    style={{background:"transparent",border:"none",cursor:"pointer",
                      fontSize:18,color:"#94A3B8",padding:"4px 8px",lineHeight:1,
                      borderRadius:4,
                    }}
                    title="Azioni">⋯</button>
                  {menuApertoIdx === idx && (
                    <div ref={menuRef} style={{
                      position:"fixed", top:menuPos.top, right:menuPos.right,
                      background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
                      boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:9999,
                      minWidth:140,padding:"4px 0",textAlign:"left",
                    }}>
                      <div onClick={() => { setMenuApertoIdx(null); onApriModifica && onApriModifica(s); }}
                        style={{padding:"8px 14px",fontSize:12,cursor:"pointer",color:"#374151",
                          display:"flex",alignItems:"center",gap:8}}
                        onMouseEnter={e => e.currentTarget.style.background="#F8FAFC"}
                        onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                        ✏️ Modifica
                      </div>
                      <div onClick={() => { setMenuApertoIdx(null); onElimina && onElimina(s.id); }}
                        style={{padding:"8px 14px",fontSize:12,cursor:"pointer",color:"#DC2626",
                          display:"flex",alignItems:"center",gap:8}}
                        onMouseEnter={e => e.currentTarget.style.background="#FEF2F2"}
                        onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                        🗑 Elimina
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PortfolioStrategie() {
  // --- Dati ---
  const [strategie, setStrategie] = useState([]);
  const [sistemi, setSistemi]     = useState([]);
  const [loadingListe, setLoadingListe] = useState(true);

  // --- Vista: "lista" | "modifica" | "analisi" ---
  const [vista, setVista] = useState("lista");

  // --- Strategia selezionata ---
  const [selId, setSelId]           = useState(null);
  const [isNuova, setIsNuova]       = useState(false);
  const [formNome, setFormNome]     = useState("");
  const [formCommenti, setFormCommenti] = useState("");
  const [formAttivo, setFormAttivo] = useState(true);
  // Tabella destinatari Nome+Email (v180 D499-D502)
  // Array di oggetti {nome, email}. Sostituisce il vecchio campo CSV formMail.
  const [formDestinatari, setFormDestinatari] = useState([]);
  // Snapshot del form caricato — per save bar dirty (v180 D500)
  const [formOriginale, setFormOriginale] = useState(null);
  const [formSistemi, setFormSistemi]       = useState(new Set());
  const [ricercaSistemi, setRicercaSistemi] = useState("");
  const [modificaSistemi, setModificaSistemi] = useState(false);

  // --- Analisi ---
  const [analisi, setAnalisi]             = useState(null);
  const [analisiLoading, setAnalisiLoading] = useState(false);
  const [analisiErrore, setAnalisiErrore] = useState(null);
  const [nomeAnalisi, setNomeAnalisi]     = useState("");

  // --- Drawer visualizza eventi ---
  const [drawerAperto, setDrawerAperto] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerPartite, setDrawerPartite] = useState([]);

  // --- Feedback ---
  const [errore, setErrore]   = useState(null);
  const [successo, setSuccesso] = useState(null);
  const [warning, setWarning]  = useState(null);
  const [salvando, setSalvando] = useState(false);

  // --- LISTA MASTER (refactor v179) ---
  // Sub-tab Dettaglio stagione / Trend storico (D498)
  const [subTab, setSubTab] = useState("dettaglio"); // "dettaglio" | "trend"
  // Toggle filtri lista master (D497)
  const [nascondiNonAttive, setNascondiNonAttive] = useState(false);
  // Filtro stagione multi-select (replica Portfolio.js)
  const [stagioniFiltro, setStagioniFiltro] = useState(new Set());
  const [dropdownAperta, setDropdownAperta] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({top:0,right:0});
  const dropdownRef = useRef(null);
  const btnDropdownRef = useRef(null);

  // ===== CARICAMENTO INIZIALE =====
  const caricaStrategie = useCallback(async () => {
    try {
      const r = await fetch(`${API}/portfolio/strategie?attivi_only=false`);
      if (!r.ok) throw new Error("Errore caricamento strategie");
      setStrategie(await r.json());
    } catch (e) { setErrore(e.message); }
  }, []);

  const caricaSistemi = useCallback(async () => {
    try {
      const r = await fetch(`${API}/sistemi`);
      if (!r.ok) throw new Error("Errore caricamento sistemi");
      setSistemi(await r.json());
    } catch (e) { setErrore(e.message); }
  }, []);

  useEffect(() => {
    Promise.all([caricaStrategie(), caricaSistemi()])
      .finally(() => setLoadingListe(false));
  }, [caricaStrategie, caricaSistemi]);

  // ===== LISTA MASTER — dropdown stagioni click-outside + derivati (refactor v179) =====
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownAperta(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggleStagione = useCallback((st) => {
    setStagioniFiltro(prev => {
      const next = new Set(prev);
      if (next.has(st)) next.delete(st); else next.add(st);
      return next;
    });
  }, []);

  // Unione stagioni disponibili da tutte le strategie
  const stagioni_disponibili = useMemo(() => {
    return [...new Set(strategie.flatMap(s => s.stagioni_lista || []))].sort();
  }, [strategie]);

  // Strategie filtrate per toggle "Nascondi non attive" (D497)
  const strategieFiltrate = useMemo(() => {
    return strategie.filter(s => {
      if (nascondiNonAttive && s.attivo === false) return false;
      return true;
    });
  }, [strategie, nascondiNonAttive]);

  // KPI strategia con filtro stagione (per footer)
  const getKpiStrategia = useCallback((s) => {
    if (stagioniFiltro.size === 0) return { eventi: s.eventi, punti: s.punti };
    const entries = Object.entries(s.dati_per_stagione || {}).filter(([st]) => stagioniFiltro.has(st));
    if (!entries.length) return { eventi: 0, punti: 0 };
    const ev = entries.reduce((a, [,v]) => a + v.eventi, 0);
    const pt = Math.round(entries.reduce((a, [,v]) => a + v.punti, 0) * 100) / 100;
    return { eventi: ev, punti: pt };
  }, [stagioniFiltro]);

  const totEventiStrategie = useMemo(() =>
    strategieFiltrate.reduce((acc, s) => acc + getKpiStrategia(s).eventi, 0),
    [strategieFiltrate, getKpiStrategia]);

  const totPuntiStrategie = useMemo(() =>
    Math.round(strategieFiltrate.reduce((acc, s) => acc + getKpiStrategia(s).punti, 0) * 100) / 100,
    [strategieFiltrate, getKpiStrategia]);

  // ===== HELPERS =====
  const resetForm = useCallback(() => {
    setSelId(null); setIsNuova(false);
    setFormNome(""); setFormCommenti(""); setFormAttivo(true);
    setFormDestinatari([]); setFormOriginale(null);
    setFormSistemi(new Set()); setRicercaSistemi(""); setModificaSistemi(false);
    setAnalisi(null); setAnalisiErrore(null);
    setErrore(null); setSuccesso(null); setWarning(null);
  }, []);

  const tornaLista = useCallback(() => {
    resetForm();
    setVista("lista");
  }, [resetForm]);

  // ===== SELEZIONA STRATEGIA (per modifica) =====
  const apriModifica = useCallback((strat) => {
    setSelId(strat.id);
    setIsNuova(false);
    setFormNome(strat.nome || "");
    setFormCommenti(strat.commenti || "");
    setFormAttivo(strat.attivo !== false);
    const mailStr = strat.indirizzi_mail || "";
    const nomiStr = strat.nomi_destinatari || "";
    // Parsa 2 CSV paralleli → array {nome, email} (v180 D502)
    const emailsArr = mailStr ? mailStr.split(",").map(m => m.trim()).filter(Boolean) : [];
    const nomiArr   = nomiStr ? nomiStr.split(",").map(n => n.trim()) : [];
    const destinatari = emailsArr.map((email, i) => ({
      nome: nomiArr[i] || "",
      email,
    }));
    setFormDestinatari(destinatari);
    const sistemiSet = new Set(strat.sistemi_ids || []);
    setFormSistemi(sistemiSet);
    // Snapshot originale per dirty detection (v180 D500)
    setFormOriginale({
      nome: strat.nome || "",
      commenti: strat.commenti || "",
      attivo: strat.attivo !== false,
      destinatari: destinatari.map(d => ({...d})),
      sistemi: Array.from(sistemiSet),
    });
    setModificaSistemi(false); setRicercaSistemi("");
    setAnalisi(null); setAnalisiErrore(null);
    setErrore(null); setSuccesso(null); setWarning(null);
    setVista("modifica");
  }, []);

  // ===== APRI ANALISI =====
  const apriAnalisi = useCallback(async (strat) => {
    setSelId(strat.id);
    setNomeAnalisi(strat.nome || "");
    setAnalisi(null); setAnalisiErrore(null);
    setVista("analisi");
    setAnalisiLoading(true);
    try {
      const r = await fetch(`${API}/strategie/analisi`, {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ strategia_id: strat.id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Errore analisi");
      }
      const data = await r.json();
      if (data.per_stagione && !data.stagioni) {
        data.stagioni = Object.entries(data.per_stagione).map(([stagione, v]) => ({ stagione, ...v }));
      }
      setAnalisi(data);
    } catch (e) { setAnalisiErrore(e.message); }
    finally { setAnalisiLoading(false); }
  }, []);

  // ===== NUOVA STRATEGIA =====
  const nuovaStrategia = useCallback(() => {
    resetForm();
    setIsNuova(true);
    setModificaSistemi(true);
    // Snapshot originale "vuoto" per dirty detection (v180 D500)
    setFormOriginale({
      nome: "",
      commenti: "",
      attivo: true,
      destinatari: [],
      sistemi: [],
    });
    setVista("modifica");
  }, [resetForm]);

  // ===== GENERA ID =====
  const generaId = () => "s" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

  // ===== SALVA =====
  const salvaStrategia = useCallback(async () => {
    if (salvando) return;
    if (!formNome.trim()) { setErrore("Il nome è obbligatorio"); return; }
    // Validazione destinatari (v180 D502)
    const REG_EMAIL = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
    for (let i = 0; i < formDestinatari.length; i++) {
      const d = formDestinatari[i];
      const emailT = (d.email || "").trim();
      const nomeT  = (d.nome  || "").trim();
      // riga vuota completa: skip silente al save
      if (!emailT && !nomeT) continue;
      if (!emailT) {
        setErrore(`Riga destinatari #${i+1}: email mancante`);
        return;
      }
      if (!REG_EMAIL.test(emailT)) {
        setErrore(`Riga destinatari #${i+1}: email non valida (${emailT})`);
        return;
      }
      if (nomeT.includes(",")) {
        setErrore(`Riga destinatari #${i+1}: il nome non può contenere virgole`);
        return;
      }
    }
    setSalvando(true);
    setErrore(null); setSuccesso(null);
    // Serializza array {nome,email} → 2 CSV paralleli (v180 D502)
    const validi = formDestinatari
      .map(d => ({ nome:(d.nome||"").trim(), email:(d.email||"").trim() }))
      .filter(d => d.email);
    const indirizziStr = validi.map(d => d.email).join(", ");
    const tuttiNomiVuoti = validi.every(d => !d.nome);
    const nomiStr = tuttiNomiVuoti ? null : validi.map(d => d.nome).join(", ");
    const idDaUsare = isNuova ? generaId() : selId;
    const body = {
      id: idDaUsare,
      nome: formNome.trim(),
      commenti: formCommenti.trim() || null,
      attivo: formAttivo,
      indirizzi_mail: indirizziStr || null,
      nomi_destinatari: nomiStr,
      sistemi: Array.from(formSistemi),
    };
    try {
      const url    = isNuova ? `${API}/strategie` : `${API}/strategie/${selId}`;
      const method = isNuova ? "POST" : "PUT";
      const r = await fetch(url, { method, headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      if (!r.ok) {
        let errMsg = "Errore salvataggio (HTTP " + r.status + ")";
        try { const err = await r.json(); errMsg = err.detail || err.message || JSON.stringify(err); } catch(_) {}
        throw new Error(errMsg);
      }
      const saved = await r.json();
      setSuccesso(isNuova ? "Strategia creata" : "Strategia aggiornata");
      if (formAttivo && !indirizziStr) setWarning("Strategia attiva senza destinatari — non riceverà notifiche");
      else setWarning(null);
      setIsNuova(false);
      setSelId(saved.id);
      setModificaSistemi(false);
      // Aggiorna snapshot originale (post-save lo stato non è più dirty)
      setFormOriginale({
        nome: formNome.trim(),
        commenti: formCommenti.trim(),
        attivo: formAttivo,
        destinatari: validi.map(d => ({...d})),
        sistemi: Array.from(formSistemi),
      });
      setFormDestinatari(validi);
      await caricaStrategie();
    } catch (e) { setErrore(e.message); }
    finally { setSalvando(false); }
  }, [formNome, formCommenti, formAttivo, formDestinatari, formSistemi, isNuova, selId, caricaStrategie, salvando]);

  // ===== ELIMINA =====
  const eliminaStrategia = useCallback(async (id) => {
    if (!window.confirm("Eliminare questa strategia?")) return;
    setErrore(null); setSuccesso(null);
    try {
      const r = await fetch(`${API}/strategie/${id}`, { method:"DELETE" });
      if (!r.ok) throw new Error("Errore eliminazione");
      setSuccesso("Strategia eliminata");
      await caricaStrategie();
    } catch (e) { setErrore(e.message); }
  }, [caricaStrategie]);

  // ===== TOGGLE SISTEMA =====
  const toggleSistema = useCallback((id) => {
    setFormSistemi(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // ===== EXPORT EXCEL =====
  const esportaExcel = useCallback(async () => {
    if (!selId) return;
    try {
      const r = await fetch(`${API}/strategie/estrai/excel`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ strategia_id: selId }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.detail || "Errore export"); return; }
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url; a.download = `strategia_${nomeAnalisi}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Errore: " + e.message); }
  }, [selId]);

  // ===== DRAWER VISUALIZZA EVENTI =====
  const apriDrawer = useCallback(async () => {
    if (!selId) return;
    setDrawerAperto(true);
    setDrawerLoading(true);
    setDrawerPartite([]);
    try {
      const r = await fetch(`${API}/strategie/estrai`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ strategia_id: selId }),
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); alert(err.detail || "Errore caricamento eventi"); setDrawerAperto(false); return; }
      const data = await r.json();
      setDrawerPartite(data.partite || []);
    } catch (e) { alert("Errore: " + e.message); setDrawerAperto(false); }
    finally { setDrawerLoading(false); }
  }, [selId]);

  // Memo per i grafici Equity / Drawdown
  const equityLine = analisi?.equity_line || [];
  const cambiStagione = useMemo(() => _calcolaCambiStagione(equityLine), [equityLine]);
  const totaleEventi = equityLine.length;
  const TooltipEquity = useMemo(() => _makeTooltipEquity(totaleEventi), [totaleEventi]);
  const TooltipDrawdown = useMemo(() => _makeTooltipDrawdown(totaleEventi), [totaleEventi]);
  // Punto di Max P/L sull'equity
  const puntoMaxPL = useMemo(() => {
    if (!equityLine.length) return null;
    let maxV = -Infinity, idxMax = -1;
    for (let i = 0; i < equityLine.length; i++) {
      const v = equityLine[i].punti_prog;
      if (v != null && v > maxV) { maxV = v; idxMax = i; }
    }
    return idxMax >= 0 ? equityLine[idxMax] : null;
  }, [equityLine]);
  // Ticks date dei cambi stagione + mappa data->etichetta stagione
  const ticksStagioni = useMemo(() => cambiStagione.map(cs => cs.data), [cambiStagione]);
  const mappaDataStagione = useMemo(() => {
    const m = {};
    cambiStagione.forEach(cs => { m[cs.data] = cs.stagione; });
    return m;
  }, [cambiStagione]);

  // ==================== DIRTY DETECTION (vista modifica, v180 D500) ====================
  // Calcolato ad ogni render: confronto stato attuale vs snapshot originale.
  const isDirty = useMemo(() => {
    if (!formOriginale) return false;
    if (formNome.trim() !== formOriginale.nome) return true;
    if (formCommenti.trim() !== formOriginale.commenti) return true;
    if (formAttivo !== formOriginale.attivo) return true;
    // Sistemi: confronto set vs array
    const sysCurr = Array.from(formSistemi).sort();
    const sysOrig = (formOriginale.sistemi || []).slice().sort();
    if (sysCurr.length !== sysOrig.length) return true;
    for (let i = 0; i < sysCurr.length; i++) {
      if (sysCurr[i] !== sysOrig[i]) return true;
    }
    // Destinatari: confronto length + ciascun nome/email trimmed (riga vuota completa = ignorata)
    const destCurr = formDestinatari
      .map(d => ({nome:(d.nome||"").trim(), email:(d.email||"").trim()}))
      .filter(d => d.nome || d.email);
    const destOrig = (formOriginale.destinatari || [])
      .map(d => ({nome:(d.nome||"").trim(), email:(d.email||"").trim()}))
      .filter(d => d.nome || d.email);
    if (destCurr.length !== destOrig.length) return true;
    for (let i = 0; i < destCurr.length; i++) {
      if (destCurr[i].nome !== destOrig[i].nome || destCurr[i].email !== destOrig[i].email) return true;
    }
    return false;
  }, [formNome, formCommenti, formAttivo, formSistemi, formDestinatari, formOriginale]);

  // ==================== RENDER ====================
  return (
    <div style={{minHeight:"100vh",background:"#FAFBFC",fontFamily:"'DM Sans',-apple-system,sans-serif"}}>

      {/* ===== HEADER (solo in vista lista) ===== */}
      {vista === "lista" && (
        <div style={{padding:"24px 32px 0 32px"}}>
          <h1 style={{fontSize:22,fontWeight:700,color:"#1E293B",marginBottom:4,letterSpacing:"-0.02em"}}>
            📋 Portfolio Strategie
          </h1>
          <p style={{fontSize:13,color:"#64748B",marginBottom:16}}>
            Analisi di performance e trend per le strategie di investimento.
          </p>
        </div>
      )}

      {/* ===== FEEDBACK ===== */}
      {errore && (
        <div style={{margin:"8px 32px 0",padding:"8px 14px",background:"#FEF2F2",
          border:"1px solid #FECACA",borderRadius:8,fontSize:12,color:"#DC2626",
          display:"flex",alignItems:"center",gap:8}}>
          <span>⚠</span> {errore}
          <button onClick={() => setErrore(null)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#DC2626",fontWeight:700}}>✕</button>
        </div>
      )}
      {successo && (
        <div style={{margin:"8px 32px 0",padding:"8px 14px",background:"#F0FDF4",
          border:"1px solid #86EFAC",borderRadius:8,fontSize:12,color:"#15803D",
          display:"flex",alignItems:"center",gap:8}}>
          <span>✓</span> {successo}
          <button onClick={() => setSuccesso(null)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#15803D",fontWeight:700}}>✕</button>
        </div>
      )}
      {warning && (
        <div style={{margin:"8px 32px 0",padding:"8px 14px",background:"#FFFBEB",
          border:"1px solid #FDE68A",borderRadius:8,fontSize:12,color:"#92400E",
          display:"flex",alignItems:"center",gap:8}}>
          <span>⚠</span> {warning}
          <button onClick={() => setWarning(null)} style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"#92400E",fontWeight:700}}>✕</button>
        </div>
      )}

      {loadingListe ? (
        <div style={{padding:60,textAlign:"center",color:"#94A3B8"}}>⏳ Caricamento dati...</div>
      ) : (
        <div style={{padding:"16px 32px 32px"}}>

          {/* ========== VISTA LISTA ========== */}
          {vista === "lista" && (
            <>
              {/* ===== CONTAINER PRINCIPALE (refactor v179.1, pattern Portfolio Sistemi) ===== */}
              <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden"}}>

                {/* ===== Barra blu — header "Strategie (N)" + + Nuova strategia + dropdown stagione (refactor v179.2) ===== */}
                <div style={{display:"flex",background:"#1F2A4A",alignItems:"center"}}>

                  {/* Gruppo sinistro: header Strategie (N) + Nuova strategia (ravvicinati) */}
                  <div style={{display:"flex",alignItems:"center",gap:8,padding:"0 16px"}}>
                    <span style={{
                      padding:"12px 8px", fontSize:13, fontWeight:700,
                      color:"#fff", fontFamily:"'DM Sans',sans-serif",
                    }}>
                      📋 Strategie ({strategie.length})
                    </span>
                    <button onClick={nuovaStrategia}
                      style={{background:"transparent",color:"#fff",fontSize:12,fontWeight:500,
                        padding:"6px 12px",border:"1px solid rgba(255,255,255,0.3)",borderRadius:6,
                        cursor:"pointer",fontFamily:"'DM Sans',sans-serif",
                        display:"inline-flex",alignItems:"center",gap:4,
                        transition:"background 0.15s"}}
                      onMouseEnter={e => e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                      onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                      ＋ Nuova strategia
                    </button>
                  </div>

                  <div style={{flex:1}}/>

                  {/* Gruppo destro: dropdown stagione + chip selezionate */}
                  <div style={{display:"flex",alignItems:"center",paddingRight:16,gap:8}}>

                    {/* Dropdown stagioni — solo sub-tab Dettaglio (D498) */}
                    {subTab === "dettaglio" && stagioni_disponibili.length > 0 && (
                      <div ref={dropdownRef} style={{position:"relative"}}>
                        <button ref={btnDropdownRef} onClick={() => {
                            if (!dropdownAperta && btnDropdownRef.current) {
                              const r = btnDropdownRef.current.getBoundingClientRect();
                              setDropdownPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
                            }
                            setDropdownAperta(o => !o);
                          }}
                          style={{...btnBase,background:"#F1F5F9",color:"#475569",fontSize:11,
                            padding:"6px 12px",border:"1px solid #E8E8E4"}}>
                          {stagioniFiltro.size === 0
                            ? "📅 Stagione ▾"
                            : `📅 ${stagioniFiltro.size} stagion${stagioniFiltro.size===1?"e":"i"} ▾`}
                        </button>
                        {dropdownAperta && (
                          <div style={{position:"fixed",top:dropdownPos.top,right:dropdownPos.right,
                            background:"#fff",border:"1px solid #E8E8E4",borderRadius:8,
                            boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:9999,
                            minWidth:160,padding:"6px 0"}}>
                            <div onClick={() => setStagioniFiltro(new Set())}
                              style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                                display:"flex",alignItems:"center",gap:8,
                                background: stagioniFiltro.size===0 ? "#EFF6FF" : "transparent",
                                color: stagioniFiltro.size===0 ? "#2563EB" : "#374151"}}>
                              <span style={{width:14,height:14,borderRadius:3,border:"1px solid #CBD5E1",
                                display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                                background: stagioniFiltro.size===0 ? "#2563EB" : "#fff",
                                color:"#fff"}}>
                                {stagioniFiltro.size===0 ? "✓" : ""}
                              </span>
                              Tutte
                            </div>
                            <div style={{height:1,background:"#F1F5F9",margin:"4px 0"}}/>
                            <div style={{maxHeight:220,overflowY:"auto"}}>
                              {stagioni_disponibili.map(st => (
                                <div key={st} onClick={() => toggleStagione(st)}
                                  style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                                    display:"flex",alignItems:"center",gap:8,
                                    background: stagioniFiltro.has(st) ? "#EFF6FF" : "transparent",
                                    color: stagioniFiltro.has(st) ? "#2563EB" : "#374151"}}>
                                  <span style={{width:14,height:14,borderRadius:3,border:"1px solid #CBD5E1",
                                    display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,
                                    background: stagioniFiltro.has(st) ? "#2563EB" : "#fff",
                                    color:"#fff"}}>
                                    {stagioniFiltro.has(st) ? "✓" : ""}
                                  </span>
                                  {st}
                                </div>
                              ))}
                            </div>
                            <div style={{height:1,background:"#F1F5F9",margin:"4px 0"}}/>
                            <div onClick={() => setDropdownAperta(false)}
                              style={{padding:"6px 14px",fontSize:12,cursor:"pointer",
                                textAlign:"center",color:"#2563EB",fontWeight:600}}>
                              Chiudi
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Chip stagioni selezionate — solo sub-tab Dettaglio */}
                    {subTab === "dettaglio" && stagioniFiltro.size > 0 && [...stagioniFiltro].sort().map(st => (
                      <span key={st} style={{fontSize:11,fontWeight:700,padding:"3px 8px",
                        borderRadius:12,background:"#2563EB",color:"#fff",
                        display:"inline-flex",alignItems:"center",gap:4}}>
                        {st}
                        <button onClick={() => toggleStagione(st)}
                          style={{background:"none",border:"none",cursor:"pointer",
                            color:"#fff",fontSize:12,padding:0,lineHeight:1}}>×</button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* ===== Barra grigia — sub-tab Dettaglio/Trend + Nascondi non attive ===== */}
                <div style={{display:"flex",background:"#F1F5F9",borderBottom:"1px solid #E8E8E4",padding:"0 16px",alignItems:"center"}}>
                  {[
                    {id:"dettaglio", label:"📊 Dettaglio stagione", info:null},
                    {id:"trend",     label:"📈 Trend storico",      info:true},
                  ].map(t => (
                    <div key={t.id} style={{display:"flex", alignItems:"center"}}>
                      <button onClick={() => setSubTab(t.id)}
                        style={{
                          padding:"8px 16px", fontSize:12, fontWeight:600,
                          border:"none", cursor:"pointer", fontFamily:"'DM Sans',sans-serif",
                          background:"transparent", transition:"all 0.15s",
                          borderBottom: subTab === t.id ? "2px solid #2563EB" : "2px solid transparent",
                          color: subTab === t.id ? "#2563EB" : "#64748B",
                          marginBottom:"-1px",
                        }}>
                        {t.label}
                      </button>
                      {t.info && subTab === t.id && (
                        <IconaInfoModal titolo="Come leggere il Trend storico">
                          <p style={{margin: "0 0 12px 0"}}>
                            Confronto delle ultime 5 stagioni per ogni strategia. Per ogni stagione vedi:
                          </p>
                          <ul style={{margin: "0 0 12px 0", paddingLeft: 20}}>
                            <li style={{marginBottom: 4}}><b>Ingressi</b>: numero di ingressi a mercato nella stagione</li>
                            <li style={{marginBottom: 4}}><b>Punti</b>: P/L totale della stagione</li>
                            <li style={{marginBottom: 4}}><b>PPE</b> (Punti Per Evento): redditività media per singolo evento</li>
                          </ul>
                          <p style={{margin: "0 0 12px 0"}}>
                            La <b>colonna evidenziata in azzurro</b> è la stagione corrente. Quando è ancora in corso, accanto al codice trovi la % di completamento (es. <code style={{fontFamily:"'IBM Plex Mono',monospace", fontSize:12}}>25-26 (35%)</code>).
                          </p>
                          <p style={{margin: "0 0 12px 0"}}>
                            Accanto al nome di ogni strategia trovi la <b>Performance storica</b>: una valutazione sintetica del rendimento complessivo (Ottima · Media · Debole) corredata da uno score 0-100%. Per i dettagli del calcolo, clicca "Come si calcola" nella legenda.
                          </p>
                          <p style={{margin: "0 0 8px 0"}}>
                            Le <b>due freccette</b> accanto a Punti e PPE indicano il <b>Trend stagionale</b>:
                          </p>
                          <ul style={{margin: "0 0 12px 0", paddingLeft: 20}}>
                            <li style={{marginBottom: 4}}><b>1ª freccia</b> → confronto con la stagione immediatamente precedente</li>
                            <li style={{marginBottom: 4}}><b>2ª freccia</b> → confronto con la media delle 3 stagioni precedenti chiuse (può includere stagioni non visibili in tabella, se la strategia ha più storico)</li>
                          </ul>
                          <p style={{margin: 0}}>
                            Una freccia <span style={{color:"#CBD5E1"}}>–</span> grigia indica che il confronto non è disponibile (es. servono almeno 3 stagioni chiuse per la media). Passa il mouse sulla cella per i valori numerici esatti.
                          </p>
                        </IconaInfoModal>
                      )}
                    </div>
                  ))}
                  <div style={{flex:1}}/>
                  <label style={{display:"inline-flex",alignItems:"center",gap:6,
                    fontSize:11,color:"#475569",cursor:"pointer",userSelect:"none",paddingRight:8}}>
                    <input type="checkbox" checked={nascondiNonAttive}
                      onChange={e => setNascondiNonAttive(e.target.checked)}
                      style={{cursor:"pointer"}}/>
                    Nascondi non attive
                  </label>
                </div>

                {/* ===== Contenuto: tabella (Dettaglio) o Trend Storico ===== */}
                {subTab === "dettaglio" ? (
                  strategie.length === 0 ? (
                    <div style={{textAlign:"center",padding:"60px 0",color:"#94A3B8",fontSize:14}}>
                      Nessuna strategia salvata. Clicca <strong>＋ Nuova strategia</strong> per iniziare.
                    </div>
                  ) : (
                    <TabellaStrategie
                      dati={strategieFiltrate}
                      onClickRiga={apriAnalisi}
                      stagioniFiltro={stagioniFiltro}
                      onApriModifica={apriModifica}
                      onElimina={eliminaStrategia}
                    />
                  )
                ) : (
                  strategie.length === 0 ? (
                    <div style={{textAlign:"center",padding:"60px 0",color:"#94A3B8",fontSize:14}}>
                      Nessuna strategia salvata. Clicca <strong>＋ Nuova strategia</strong> per iniziare.
                    </div>
                  ) : (
                    <PortfolioTrendStorico items={strategieFiltrate} tipo="strategia" />
                  )
                )}

                {/* ===== Footer aggregato — solo sub-tab Dettaglio ===== */}
                {subTab === "dettaglio" && strategieFiltrate.length > 0 && (
                  <div style={{padding:"10px 16px",background:"#F8FAFC",
                    borderTop:"1px solid #E8E8E4",fontSize:11,color:"#94A3B8",
                    display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span>{strategieFiltrate.length} {strategieFiltrate.length===1?"strategia":"strategie"} · {totEventiStrategie.toLocaleString("it-IT")} eventi totali</span>
                    <span>Punti totali: <strong style={{color:colPunti(totPuntiStrategie)}}>{fmtPunti(totPuntiStrategie)}</strong></span>
                  </div>
                )}

              </div>
            </>
          )}

          {/* ========== VISTA MODIFICA ========== */}
          {vista === "modifica" && (
            <div style={{maxWidth:720,margin:"0 auto",paddingBottom:80}}>
              {/* Breadcrumb */}
              <button onClick={tornaLista}
                style={{...btnGhost,fontSize:12,padding:"6px 12px",marginBottom:20}}>
                ← Portfolio Strategie
              </button>

              <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,padding:"24px 28px"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",
                  letterSpacing:"0.06em",marginBottom:24}}>
                  {isNuova ? "Nuova strategia" : "Modifica strategia"}
                </div>

                {/* ===== SEZIONE: ANAGRAFICA ===== */}
                <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",
                  letterSpacing:"0.08em",marginBottom:10,paddingBottom:6,
                  borderBottom:"1px solid #F1F5F9"}}>Anagrafica</div>

                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>
                  Nome <span style={{color:"#DC2626"}}>*</span>
                </label>
                <input value={formNome} onChange={e => setFormNome(e.target.value)}
                  style={{...inputStyle,marginBottom:16}} placeholder="Nome strategia" />

                <label style={{fontSize:12,fontWeight:600,color:"#374151",display:"block",marginBottom:4}}>
                  Commenti
                </label>
                <textarea value={formCommenti} onChange={e => setFormCommenti(e.target.value)}
                  rows={3} style={{...inputStyle,marginBottom:24,resize:"vertical"}}
                  placeholder="Note opzionali" />

                {/* ===== SEZIONE: STATO ===== */}
                <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",
                  letterSpacing:"0.08em",marginBottom:10,paddingBottom:6,
                  borderBottom:"1px solid #F1F5F9"}}>Stato</div>

                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24}}>
                  <label style={{fontSize:12,fontWeight:600,color:"#374151"}}>Attiva strategia</label>
                  <button onClick={() => setFormAttivo(a => !a)}
                    style={{width:42,height:24,borderRadius:12,border:"none",cursor:"pointer",
                      background: formAttivo ? "#16A34A" : "#D4D4D4",
                      position:"relative",transition:"background 0.2s"}}>
                    <div style={{width:18,height:18,borderRadius:9,background:"#fff",
                      position:"absolute",top:3,
                      left: formAttivo ? 21 : 3,
                      transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}} />
                  </button>
                  <span style={{fontSize:12,color: formAttivo ? "#15803D" : "#94A3B8",fontWeight:600}}>
                    {formAttivo ? "Attiva" : "Non attiva"}
                  </span>
                </div>

                {/* ===== SEZIONE: DESTINATARI (solo se attiva) ===== */}
                {formAttivo && (
                  <>
                    <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",
                      letterSpacing:"0.08em",marginBottom:10,paddingBottom:6,
                      borderBottom:"1px solid #F1F5F9"}}>Destinatari</div>

                    <div style={{fontSize:11,color:"#94A3B8",marginBottom:10,
                      display:"flex",alignItems:"flex-start",gap:4}}>
                      <span>ℹ️</span>
                      <span>Riceveranno le selezioni settimanali e i bilanci automatici. Il nome è opzionale.</span>
                    </div>

                    {formDestinatari.length > 0 && (
                      <table style={{width:"100%",borderCollapse:"collapse",marginBottom:8,
                        fontSize:12,fontFamily:"'DM Sans',sans-serif"}}>
                        <thead>
                          <tr style={{background:"#F8FAFC"}}>
                            <th style={{textAlign:"left",padding:"6px 8px",fontSize:10,fontWeight:700,
                              color:"#64748B",textTransform:"uppercase",letterSpacing:"0.06em",
                              borderBottom:"1px solid #E2E8F0",width:"40%"}}>Nome</th>
                            <th style={{textAlign:"left",padding:"6px 8px",fontSize:10,fontWeight:700,
                              color:"#64748B",textTransform:"uppercase",letterSpacing:"0.06em",
                              borderBottom:"1px solid #E2E8F0"}}>Email</th>
                            <th style={{width:36,borderBottom:"1px solid #E2E8F0"}}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formDestinatari.map((d, idx) => {
                            const REG_EMAIL = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
                            const emailT = (d.email || "").trim();
                            const nomeT  = (d.nome  || "").trim();
                            const emailInvalida = emailT.length > 0 && !REG_EMAIL.test(emailT);
                            const nomeInvalido  = nomeT.includes(",");
                            return (
                              <tr key={idx} style={{borderBottom:"1px solid #F1F5F9"}}>
                                <td style={{padding:"4px 4px"}}>
                                  <input value={d.nome}
                                    onChange={e => {
                                      const next = formDestinatari.slice();
                                      next[idx] = {...next[idx], nome: e.target.value};
                                      setFormDestinatari(next);
                                    }}
                                    placeholder="es. Mario Rossi"
                                    style={{...inputStyle,padding:"6px 8px",fontSize:12,marginBottom:0,
                                      borderColor: nomeInvalido ? "#DC2626" : "#E8E8E4"}} />
                                  {nomeInvalido && (
                                    <div style={{fontSize:10,color:"#DC2626",marginTop:2}}>
                                      Il nome non può contenere virgole
                                    </div>
                                  )}
                                </td>
                                <td style={{padding:"4px 4px"}}>
                                  <input value={d.email}
                                    onChange={e => {
                                      const next = formDestinatari.slice();
                                      next[idx] = {...next[idx], email: e.target.value};
                                      setFormDestinatari(next);
                                    }}
                                    placeholder="email@esempio.com"
                                    style={{...inputStyle,padding:"6px 8px",fontSize:12,marginBottom:0,
                                      borderColor: emailInvalida ? "#DC2626" : "#E8E8E4"}} />
                                  {emailInvalida && (
                                    <div style={{fontSize:10,color:"#DC2626",marginTop:2}}>
                                      Formato email non valido
                                    </div>
                                  )}
                                </td>
                                <td style={{padding:"4px 4px",textAlign:"center"}}>
                                  <button
                                    title="Rimuovi destinatario"
                                    onClick={() => {
                                      const next = formDestinatari.filter((_, j) => j !== idx);
                                      setFormDestinatari(next);
                                    }}
                                    style={{background:"none",border:"none",cursor:"pointer",
                                      color:"#94A3B8",fontSize:16,padding:"2px 6px",lineHeight:1,
                                      borderRadius:4,transition:"background 0.12s,color 0.12s"}}
                                    onMouseEnter={e => {
                                      e.currentTarget.style.background = "#FEE2E2";
                                      e.currentTarget.style.color = "#DC2626";
                                    }}
                                    onMouseLeave={e => {
                                      e.currentTarget.style.background = "transparent";
                                      e.currentTarget.style.color = "#94A3B8";
                                    }}
                                  >🗑️</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}

                    <button
                      onClick={() => setFormDestinatari([...formDestinatari, {nome:"", email:""}])}
                      style={{...btnGhost,fontSize:11,padding:"5px 10px",marginBottom:24}}>
                      ＋ Aggiungi destinatario
                    </button>
                  </>
                )}

                {/* ===== SEZIONE: SISTEMI ASSOCIATI ===== */}
                <div style={{fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",
                  letterSpacing:"0.08em",marginBottom:10,paddingBottom:6,
                  borderBottom:"1px solid #F1F5F9"}}>
                  Sistemi associati{" "}
                  <span style={{fontWeight:500,color:"#94A3B8",letterSpacing:0,textTransform:"none"}}>
                    ({formSistemi.size} selezionati)
                  </span>
                </div>

                {/* Chip sistemi selezionati */}
                {formSistemi.size > 0 && (
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:8}}>
                    {Array.from(formSistemi).map(sid => {
                      const sys = sistemi.find(s => s.id === sid);
                      return (
                        <span key={sid} style={{fontSize:11,fontWeight:600,color:"#1D4ED8",
                          background:"#EFF4FF",border:"1px solid #BFDBFE",borderRadius:12,
                          padding:"2px 8px",display:"inline-flex",alignItems:"center",gap:4}}>
                          {sys ? sys.nome : sid}
                          <button onClick={() => toggleSistema(sid)}
                            style={{background:"none",border:"none",cursor:"pointer",
                              color:"#93C5FD",fontSize:12,padding:0,lineHeight:1,fontWeight:700}}>✕</button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Toggle mostra selezione */}
                {!modificaSistemi && !isNuova && (
                  <button onClick={() => setModificaSistemi(true)}
                    style={{...btnGhost,fontSize:11,padding:"5px 10px",marginBottom:8}}>
                    ＋ Modifica sistemi
                  </button>
                )}

                {/* Pannello selezione sistemi */}
                {(modificaSistemi || isNuova) && sistemi.length > 0 && (
                  <div style={{border:"1px solid #E8E8E4",borderRadius:8,overflow:"hidden",marginBottom:16}}>
                    <div style={{padding:"6px 8px",borderBottom:"1px solid #F0F0EC"}}>
                      <input value={ricercaSistemi}
                        onChange={e => setRicercaSistemi(e.target.value)}
                        placeholder="Cerca sistema..."
                        style={{...inputStyle,fontSize:11,padding:"5px 8px",marginBottom:0}} />
                    </div>
                    <div style={{maxHeight:220,overflowY:"auto"}}>
                      {sistemi
                        .filter(sys => sys.attivo)
                        .filter(sys => !ricercaSistemi ||
                          sys.nome.toLowerCase().includes(ricercaSistemi.toLowerCase()) ||
                          sys.id.toLowerCase().includes(ricercaSistemi.toLowerCase()))
                        .map(sys => {
                          const isSelected = formSistemi.has(sys.id);
                          return (
                            <div key={sys.id} onClick={() => toggleSistema(sys.id)}
                              style={{padding:"7px 10px",cursor:"pointer",
                                borderBottom:"1px solid #F4F4F0",fontSize:12,
                                display:"flex",alignItems:"center",gap:6,
                                background: isSelected ? "#EFF4FF" : "transparent",
                                transition:"background 0.12s"}}>
                              <span style={{width:18,height:18,borderRadius:4,
                                display:"flex",alignItems:"center",justifyContent:"center",
                                fontSize:11,fontWeight:700,
                                background: isSelected ? "#2563EB" : "#E8E8E4",
                                color: isSelected ? "#fff" : "transparent",
                                transition:"all 0.12s"}}>✓</span>
                              <span style={{fontWeight:600,color: isSelected ? "#1D4ED8" : "#1A1A18"}}>
                                {sys.nome}
                              </span>
                              {sys.mercato && (
                                <span style={{fontSize:9,color:"#94A3B8",marginLeft:"auto",whiteSpace:"nowrap"}}>
                                  {sys.mercato}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    {!isNuova && (
                      <div style={{padding:"4px 8px",borderTop:"1px solid #F0F0EC",textAlign:"right"}}>
                        <button onClick={() => { setModificaSistemi(false); setRicercaSistemi(""); }}
                          style={{...btnGhost,fontSize:10,padding:"3px 8px"}}>Chiudi</button>
                      </div>
                    )}
                  </div>
                )}
                {sistemi.length === 0 && (
                  <div style={{fontSize:12,color:"#94A3B8",padding:"8px 0",marginBottom:8}}>
                    Nessun sistema disponibile
                  </div>
                )}

                {/* Pulsanti finali in fondo alla card */}
                <div style={{display:"flex",gap:8,marginTop:20,paddingTop:16,borderTop:"1px solid #F0F0EC"}}>
                  <button onClick={salvaStrategia} disabled={salvando}
                    style={{...btnSave,opacity:salvando?0.6:1}}>
                    {salvando ? "⏳ Salvataggio..." : "💾 Salva"}
                  </button>
                  <button onClick={tornaLista} style={btnGhost}>
                    Annulla
                  </button>
                </div>
              </div>

              {/* ===== SAVE BAR STICKY (v180 D500) — appare solo se dirty ===== */}
              {isDirty && (
                <div style={{
                  position:"fixed",left:0,right:0,bottom:0,
                  background:"#FFFBEB",borderTop:"1px solid #FCD34D",
                  padding:"10px 24px",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:14,
                  boxShadow:"0 -2px 8px rgba(0,0,0,0.08)",zIndex:1000,
                  fontFamily:"'DM Sans',sans-serif",
                }}>
                  <span style={{fontSize:12,color:"#92400E",fontWeight:600}}>
                    ⚠️ Modifiche non salvate
                  </span>
                  <button onClick={salvaStrategia} disabled={salvando}
                    style={{...btnSave,fontSize:12,padding:"6px 14px",opacity:salvando?0.6:1}}>
                    {salvando ? "⏳ Salvataggio..." : "💾 Salva modifiche"}
                  </button>
                  <button onClick={tornaLista}
                    style={{...btnGhost,fontSize:12,padding:"6px 14px"}}>
                    Annulla
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ========== VISTA ANALISI ========== */}
          {vista === "analisi" && (
            <div>
              {/* Header scheda Analisi strategia (coerenza con Analisi Sistemi) */}
              <h1 style={{fontSize:22,fontWeight:700,color:"#1E293B",marginBottom:4,letterSpacing:"-0.02em"}}>
                📈 Analisi Strategia
              </h1>
              <p style={{fontSize:13,color:"#64748B",marginBottom:16}}>
                Visualizza le statistiche e le performance della strategia.
              </p>
              {/* Riga 1: Torna a Portfolio Strategie + Estrai eventi */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                <button onClick={tornaLista}
                  style={{...btnGhost,fontSize:12,padding:"6px 12px"}}>
                  ← Portfolio Strategie
                </button>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={apriDrawer}
                    style={{...btnGhost,fontSize:12,padding:"6px 14px"}}>
                    👁 Visualizza eventi
                  </button>
                  <button onClick={esportaExcel} style={{...btnSave,fontSize:12,padding:"6px 14px"}}>
                    📥 Estrai eventi
                  </button>
                </div>
              </div>
              {/* Riga 2: Nome + badge stato + loading/errore (pattern AnalisiSistemi) */}
              <div style={{marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:16,fontWeight:700,color:"#1E293B"}}>{nomeAnalisi}</span>
                {(() => {
                  const strat = strategie.find(s => s.id === selId);
                  const attivo = strat?.attivo === true;
                  return (
                    <span style={{
                      fontSize:11,fontWeight:700,padding:"2px 10px",borderRadius:12,
                      background: attivo ? "#DCFCE7" : "#F1F5F9",
                      color: attivo ? "#15803D" : "#94A3B8",
                    }}>
                      {attivo ? "✅ Attiva" : "⬜ Non attiva"}
                    </span>
                  );
                })()}
                {analisiLoading && <span style={{fontSize:12,color:"#94A3B8"}}>⏳ Caricamento analisi...</span>}
                {analisiErrore && <span style={{fontSize:12,color:"#DC2626"}}>⚠ {analisiErrore}</span>}
              </div>

              {!analisi && !analisiLoading && !analisiErrore && (
                <div style={{textAlign:"center",padding:60,color:"#94A3B8",fontSize:14}}>
                  Nessun dato disponibile.
                </div>
              )}

              {analisi && (
                <>
                  {/* 12 KPI */}
                  <div style={{display:"flex",flexWrap:"wrap",background:"#fff",
                    border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                    {[
                      {label:"Eventi",              val: analisi.eventi?.toLocaleString("it-IT"),                                                    color:"#1D4ED8"},
                      {label:"Punti Totali",        val: (analisi.punti >= 0 ? "+" : "") + analisi.punti?.toFixed(2),                                color: analisi.punti >= 0 ? "#15803D" : "#DC2626"},
                      {label:"PPE",                 val: (analisi.ppe >= 0 ? "+" : "") + analisi.ppe?.toFixed(2),                                    color: analisi.ppe >= 0 ? "#15803D" : "#DC2626"},
                      {label:"Win Rate",            val: analisi.pct_vincenti?.toFixed(1) + "%",                                                     color:"#1D4ED8"},
                      {label:"Quota Media",         val: analisi.quota_media === 0 ? "N/A" : analisi.quota_media?.toFixed(2),                        color:"#1D4ED8"},
                      {label:"Max Win Run",         val: analisi.max_win_run,                                                                         color:"#15803D"},
                      {label:"Max Lose Run",        val: analisi.max_lose_run,                                                                        color:"#DC2626"},
                      {label:"Max P/L",             val: (() => {
                        const eq = analisi.equity_line || [];
                        if (!eq.length) return null;
                        const mx = Math.max(...eq.map(p => p.punti_prog || 0), 0);
                        return mx.toFixed(2);
                      })(),                                                                                                                             color:"#15803D"},
                      {label:"Max Drawdown",        val: analisi.max_drawdown?.toFixed(2),                                                           color:"#DC2626"},
                      {label:"Esp. Media",          val: analisi.esp_media?.toFixed(2),                                                              color:"#7C3AED", info: INFO_ESP_MEDIA_STRATEGIA},
                      {label:"Esp. Max Giorn.",     val: analisi.espo_max_giornaliera?.toFixed(2),                                                   color:"#7C3AED", info: INFO_ESP_GIORN_STRATEGIA},
                      {label:"Esp. Max Sett.",      val: analisi.espo_max_settimanale?.toFixed(2),                                                   color:"#7C3AED", info: INFO_ESP_SETT_STRATEGIA},
                      {label:"Size Ingresso",       val: analisi.size_ingresso != null ? (analisi.size_ingresso*100).toFixed(2)+"%" : "—",           color:"#0369A1"},
                      {label:"Esp. Max Sett. Cap.", val: analisi.esp_max_set_capitale != null ? analisi.esp_max_set_capitale?.toFixed(1)+"%" : "—",  color:"#0369A1"},
                    ].map(({label,val,color,info}) => (
                      <div key={label} style={{flex:"1 1 100px",minWidth:100,
                        padding:"8px 12px",borderRight:"1px solid #F0F0EC",
                        borderBottom:"1px solid #F0F0EC",textAlign:"center"}}>
                        <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",
                          textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3,
                          display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{label}{info}</div>
                        <div style={{fontSize:15,fontWeight:700,color,
                          fontFamily:"'DM Sans',sans-serif"}}>{val ?? "—"}</div>
                      </div>
                    ))}
                  </div>

                  {/* Variazione nel tempo */}
                  {analisi.variazione_tempo && analisi.variazione_tempo.length > 0 && (
                    <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"6px 14px",background:"#1B2846"}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Variazione nel tempo</span>
                      </div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                        <thead>
                          <tr>
                            {["Statistica",...analisi.variazione_tempo.map(v => v.periodo)].map(h => (
                              <th key={h} style={{background:"#E8EDF5",padding:"5px 10px",
                                textAlign:h==="Statistica"?"left":"right",fontSize:9,fontWeight:700,color:"#1B2846",
                                textTransform:"uppercase",letterSpacing:"0.04em",borderBottom:"1px solid #E8E8E4"}}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {["Punti","Eventi","PPE"].map(stat => (
                            <tr key={stat}>
                              <td style={{padding:"5px 10px",fontWeight:600,borderBottom:"1px solid #F4F4F0"}}>{stat}</td>
                              {analisi.variazione_tempo.map(v => {
                                const val = stat==="Punti"?v.punti:stat==="Eventi"?v.eventi:v.ppe;
                                const isNum = stat!=="Eventi";
                                return (
                                  <td key={v.periodo} style={{padding:"5px 10px",textAlign:"right",
                                    fontFamily:"'DM Sans',sans-serif",
                                    color:isNum?(val>=0?"#15803D":"#DC2626"):"#1A1A18",
                                    fontWeight:stat==="Punti"?600:400,borderBottom:"1px solid #F4F4F0"}}>
                                    {stat==="Eventi"?val:(val>=0?"+":"")+val?.toFixed(2)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Breakdown stagioni */}
                  {analisi.stagioni && analisi.stagioni.length > 0 && (
                    <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"6px 14px",background:"#1B2846"}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Breakdown per stagione</span>
                      </div>
                      <div style={{maxHeight:220,overflowY:"auto"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                          <thead>
                            <tr>
                              {["Stagione","Eventi","% Ev.","Punti","PPE","% Vinc.","Max DD","Esp. Media","Esp. Max Giorn.","Esp. Max Sett."].map(h => (
                                <th key={h} style={{position:"sticky",top:0,background:"#E8EDF5",padding:"5px 10px",
                                  textAlign: h==="Stagione"?"left":"right",fontSize:9,fontWeight:700,color:"#1B2846",
                                  textTransform:"uppercase",letterSpacing:"0.04em",borderBottom:"1px solid #E8E8E4"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {analisi.stagioni.map(s => (
                              <tr key={s.stagione}>
                                <td style={{padding:"4px 10px",fontWeight:600,borderBottom:"1px solid #F4F4F0"}}>{s.stagione}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",borderBottom:"1px solid #F4F4F0"}}>{s.eventi?.toLocaleString("it-IT")}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",borderBottom:"1px solid #F4F4F0"}}>{s.pct_ev?.toFixed(1)}%</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontWeight:600,color:s.punti>=0?"#15803D":"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{s.punti>=0?"+":""}{s.punti?.toFixed(2)}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:s.ppe>=0?"#15803D":"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{s.ppe>=0?"+":""}{s.ppe?.toFixed(2)}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#1D4ED8",borderBottom:"1px solid #F4F4F0"}}>{s.pct_vincenti!=null?s.pct_vincenti.toFixed(1)+"%":"—"}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{s.max_drawdown!=null?s.max_drawdown.toFixed(2):"—"}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",borderBottom:"1px solid #F4F4F0"}}>{s.esp_media!=null?s.esp_media.toFixed(2):"—"}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",borderBottom:"1px solid #F4F4F0"}}>{s.espo_max_giornaliera!=null?s.espo_max_giornaliera.toFixed(2):"—"}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#7C3AED",borderBottom:"1px solid #F4F4F0"}}>{s.espo_max_settimanale!=null?s.espo_max_settimanale.toFixed(2):"—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Equity Line */}
                  {analisi.equity_line && analisi.equity_line.length > 0 && (
                    <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"6px 14px",background:"#1B2846"}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Equity Line</span>

                      </div>
                      <div style={{padding:"12px 8px 8px"}}>
                        <ResponsiveContainer width="100%" height={320}>
                          <LineChart data={analisi.equity_line} margin={{top:24,right:20,bottom:24,left:10}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EC" />
                            <XAxis dataKey="data"
                              ticks={ticksStagioni}
                              tickFormatter={(v) => mappaDataStagione[v] || ""}
                              tick={{fontSize:10,fill:"#64748B",fontFamily:"'DM Sans',sans-serif"}}
                              axisLine={{stroke:"#CBD5E1"}} tickLine={true}
                              interval={0} height={22} />
                            <YAxis tick={{fontSize:10,fill:"#94A3B8",fontFamily:"'DM Sans',sans-serif"}} tickFormatter={(v) => v.toFixed(1)} />
                            <Tooltip content={<TooltipEquity />} />
                            <ReferenceLine y={0} stroke="#D8D8D4" strokeDasharray="4 4" />
                            {cambiStagione.map(cs => (
                              <ReferenceLine
                                key={`eq-${cs.idx}`}
                                x={cs.data}
                                stroke="#CBD5E1" strokeDasharray="2 3"
                              />
                            ))}
                            <Line type="monotone" dataKey="punti_prog" stroke="#2563EB" strokeWidth={2}
                              dot={(props) => {
                                const { cx, cy, payload, index } = props;
                                if (!puntoMaxPL || payload.n !== puntoMaxPL.n) return null;
                                const label = `Max +${puntoMaxPL.punti_prog.toFixed(2)}`;
                                const charW = 6.5;
                                const padX = 6;
                                const rectW = label.length * charW + padX * 2;
                                const rectH = 16;
                                return (
                                  <g key={`maxpl-${index}`} style={{pointerEvents:"none"}}>
                                    <rect x={cx - rectW/2} y={cy - 22 - rectH/2}
                                      width={rectW} height={rectH} rx={4}
                                      fill="#fff" stroke="#15803D" strokeWidth={1} />
                                    <text x={cx} y={cy - 22 + 4} textAnchor="middle"
                                      fontSize={11} fontWeight={700} fill="#15803D"
                                      fontFamily="'DM Sans',sans-serif">
                                      {label}
                                    </text>
                                    <circle cx={cx} cy={cy} r={6} fill="#15803D" stroke="#fff" strokeWidth={2} />
                                  </g>
                                );
                              }}
                              activeDot={{r:4,fill:"#2563EB"}} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Grafico Drawdown */}
                  {analisi.equity_line && analisi.equity_line.length > 0 && (
                    <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"6px 14px",background:"#1B2846"}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Drawdown</span>
                      </div>
                      <div style={{padding:"12px 8px 8px"}}>
                        <ResponsiveContainer width="100%" height={200}>
                          <AreaChart data={analisi.equity_line} margin={{top:5,right:20,bottom:24,left:10}}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EC" />
                            <XAxis dataKey="data"
                              ticks={ticksStagioni}
                              tickFormatter={(v) => mappaDataStagione[v] || ""}
                              tick={{fontSize:10,fill:"#64748B",fontFamily:"'DM Sans',sans-serif"}}
                              axisLine={{stroke:"#CBD5E1"}} tickLine={true}
                              interval={0} height={22} />
                            <YAxis tick={{fontSize:10,fill:"#94A3B8",fontFamily:"'DM Sans',sans-serif"}}
                              tickFormatter={(v) => v === 0 ? "0" : `-${v.toFixed(1)}`}
                              reversed={true}
                              domain={[0, 'dataMax']} />
                            <Tooltip content={<TooltipDrawdown />} />
                            {cambiStagione.map(cs => (
                              <ReferenceLine
                                key={`dd-${cs.idx}`}
                                x={cs.data}
                                stroke="#CBD5E1" strokeDasharray="2 3"
                              />
                            ))}
                            <Area type="monotone" dataKey="drawdown" stroke="#DC2626" strokeWidth={1.5} fill="#FECACA" fillOpacity={0.6} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Breakdown lega */}
                  {analisi.per_lega && Object.keys(analisi.per_lega).length > 0 && (
                    <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                      <div style={{padding:"6px 14px",background:"#1B2846"}}>
                        <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Breakdown per lega</span>
                      </div>
                      <div style={{maxHeight:280,overflowY:"auto"}}>
                        <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                          <thead>
                            <tr>
                              {["Lega","Eventi","% Ev.","Punti","PPE","% Vinc.","Max DD"].map(h => (
                                <th key={h} style={{position:"sticky",top:0,background:"#E8EDF5",padding:"5px 10px",
                                  textAlign:h==="Lega"?"left":"right",fontSize:9,fontWeight:700,color:"#1B2846",
                                  textTransform:"uppercase",letterSpacing:"0.04em",borderBottom:"1px solid #E8E8E4"}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(analisi.per_lega).map(([lega,v]) => (
                              <tr key={lega}>
                                <td style={{padding:"4px 10px",fontWeight:600,borderBottom:"1px solid #F4F4F0",fontSize:10}}>{lega}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",borderBottom:"1px solid #F4F4F0"}}>{v.eventi}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#6B6B66",borderBottom:"1px solid #F4F4F0"}}>{v.pct_ev?.toFixed(1)}%</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontWeight:600,color:v.punti>=0?"#15803D":"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{v.punti>=0?"+":""}{v.punti?.toFixed(2)}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:v.ppe>=0?"#15803D":"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{v.ppe>=0?"+":""}{v.ppe?.toFixed(2)}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#1D4ED8",borderBottom:"1px solid #F4F4F0"}}>{v.pct_vincenti!=null?v.pct_vincenti.toFixed(1)+"%":"—"}</td>
                                <td style={{padding:"4px 10px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",color:"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{v.max_drawdown!=null?v.max_drawdown.toFixed(2):"—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Punti stagione/lega */}
                  {analisi.punti_stagione_lega && Object.keys(analisi.punti_stagione_lega).length > 0 && (() => {
                    const fmtP = v => v!=null?(v>=0?"+":"")+v.toFixed(2):"—";
                    const colP = v => v==null?"#94A3B8":v>=0?"#15803D":"#DC2626";
                    const stagioni = Object.keys(analisi.punti_stagione_lega).filter(k=>!k.startsWith("_")).sort();
                    const legheSet = new Set();
                    stagioni.forEach(s => Object.keys(analisi.punti_stagione_lega[s]).filter(k=>!k.startsWith("_")).forEach(l=>legheSet.add(l)));
                    const leghe = [...legheSet].sort();
                    const totLega = {};
                    leghe.forEach(l => { totLega[l]=stagioni.reduce((a,s)=>a+(analisi.punti_stagione_lega[s]?.[l]||0),0); });
                    const totGlob = analisi.punti_stagione_lega["_Totale"]?.["_TOT"];
                    return (
                      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                        <div style={{padding:"6px 14px",background:"#1B2846"}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Punti Stagione / Lega</span>
                        </div>
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                            <thead><tr>
                              <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                              {leghe.map(l=>(<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                              <th style={{position:"sticky",right:0,background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",borderLeft:"2px solid #CBD5E1",zIndex:2}}>TOT</th>
                            </tr></thead>
                            <tbody>
                              {stagioni.map(stag=>(
                                <tr key={stag}>
                                  <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:1}}>{stag}</td>
                                  {leghe.map(lega=>{const val=analisi.punti_stagione_lega[stag]?.[lega];return(<td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:colP(val),borderBottom:"1px solid #F4F4F0"}}>{val!=null?fmtP(val):"—"}</td>);})}
                                  <td style={{position:"sticky",right:0,background:"#fff",padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(analisi.punti_stagione_lega[stag]?._TOT),borderBottom:"1px solid #F4F4F0",borderLeft:"2px solid #CBD5E1",zIndex:1}}>{fmtP(analisi.punti_stagione_lega[stag]?._TOT)}</td>
                                </tr>
                              ))}
                              <tr style={{background:"#F8F8F5"}}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:1}}>Totale</td>
                                {leghe.map(l=>(<td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(totLega[l]),borderBottom:"1px solid #E8E8E4"}}>{fmtP(totLega[l])}</td>))}
                                <td style={{position:"sticky",right:0,background:"#F8F8F5",padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colP(totGlob),borderBottom:"1px solid #E8E8E4",borderLeft:"2px solid #CBD5E1",zIndex:1}}>{fmtP(totGlob)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Eventi stagione/lega */}
                  {analisi.eventi_stagione_lega && Object.keys(analisi.eventi_stagione_lega).length > 0 && (() => {
                    const stagioni = Object.keys(analisi.eventi_stagione_lega).sort();
                    const legheSet = new Set();
                    stagioni.forEach(s=>Object.keys(analisi.eventi_stagione_lega[s]).forEach(l=>legheSet.add(l)));
                    const leghe=[...legheSet].sort();
                    const totStag={};
                    stagioni.forEach(s=>{totStag[s]=Object.values(analisi.eventi_stagione_lega[s]).reduce((a,b)=>a+b,0);});
                    const totLega={};
                    leghe.forEach(l=>{totLega[l]=stagioni.reduce((a,s)=>a+(analisi.eventi_stagione_lega[s]?.[l]||0),0);});
                    const totGlob=Object.values(totStag).reduce((a,b)=>a+b,0);
                    return (
                      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                        <div style={{padding:"6px 14px",background:"#1B2846"}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Eventi Stagione / Lega</span>
                        </div>
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                            <thead><tr>
                              <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                              {leghe.map(l=>(<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                              <th style={{position:"sticky",right:0,background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",borderLeft:"2px solid #CBD5E1",zIndex:2}}>TOT</th>
                            </tr></thead>
                            <tbody>
                              {stagioni.map(stag=>(
                                <tr key={stag}>
                                  <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:1}}>{stag}</td>
                                  {leghe.map(lega=>{const val=analisi.eventi_stagione_lega[stag]?.[lega];return(<td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:val>0?"#1A1A18":"#94A3B8",borderBottom:"1px solid #F4F4F0"}}>{val!=null?val:"—"}</td>);})}
                                  <td style={{position:"sticky",right:0,background:"#fff",padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1D4ED8",borderBottom:"1px solid #F4F4F0",borderLeft:"2px solid #CBD5E1",zIndex:1}}>{totStag[stag]}</td>
                                </tr>
                              ))}
                              <tr style={{background:"#F8F8F5"}}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:1}}>Totale</td>
                                {leghe.map(l=>(<td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1A1A18",borderBottom:"1px solid #E8E8E4"}}>{totLega[l]}</td>))}
                                <td style={{position:"sticky",right:0,background:"#F8F8F5",padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:"#1D4ED8",borderBottom:"1px solid #E8E8E4",borderLeft:"2px solid #CBD5E1",zIndex:1}}>{totGlob}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* PPE stagione/lega */}
                  {analisi.punti_stagione_lega && analisi.eventi_stagione_lega && Object.keys(analisi.punti_stagione_lega).length > 0 && (() => {
                    const fmtPPE = v => v==null?"—":(v>=0?"+":"")+v.toFixed(2);
                    const colPPE = v => v==null?"#94A3B8":v>=0?"#15803D":"#DC2626";
                    const stagioni = Object.keys(analisi.punti_stagione_lega).filter(k=>!k.startsWith("_")).sort();
                    const legheSet = new Set();
                    stagioni.forEach(s => Object.keys(analisi.punti_stagione_lega[s]).filter(k=>!k.startsWith("_")).forEach(l=>legheSet.add(l)));
                    const leghe = [...legheSet].sort();
                    const mediaPond = {};
                    leghe.forEach(l => {
                      const sumPunti = stagioni.reduce((a,s)=>a+(analisi.punti_stagione_lega[s]?.[l]||0),0);
                      const sumEventi = stagioni.reduce((a,s)=>a+(analisi.eventi_stagione_lega[s]?.[l]||0),0);
                      mediaPond[l] = sumEventi > 0 ? sumPunti/sumEventi : null;
                    });
                    const tooltipMedia = "Media ponderata: somma punti / somma eventi per ciascuna lega. Le stagioni con più eventi pesano di più.";
                    return (
                      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                        <div style={{padding:"6px 14px",background:"#1B2846"}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>PPE Stagione / Lega</span>
                        </div>
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"max-content",borderCollapse:"collapse",fontSize:10}}>
                            <thead><tr>
                              <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",zIndex:2}}>Stag.\Lega</th>
                              {leghe.map(l=>(<th key={l} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{l}</th>))}
                            </tr></thead>
                            <tbody>
                              {stagioni.map(stag=>(
                                <tr key={stag}>
                                  <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:1}}>{stag}</td>
                                  {leghe.map(lega=>{
                                    const punti=analisi.punti_stagione_lega[stag]?.[lega];
                                    const ev=analisi.eventi_stagione_lega[stag]?.[lega];
                                    const ppe=(ev!=null&&ev>0&&punti!=null)?punti/ev:null;
                                    return(<td key={lega} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:colPPE(ppe),borderBottom:"1px solid #F4F4F0"}}>{fmtPPE(ppe)}</td>);
                                  })}
                                </tr>
                              ))}
                              <tr style={{background:"#F8F8F5"}} title={tooltipMedia}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:1,cursor:"help"}}>Media</td>
                                {leghe.map(l=>(<td key={l} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:colPPE(mediaPond[l]),borderBottom:"1px solid #E8E8E4"}}>{fmtPPE(mediaPond[l])}</td>))}
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Punti per mese */}
                  {analisi.punti_mese && Object.keys(analisi.punti_mese).length > 0 && (() => {
                    const mesi=["Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre","Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","TOT"];
                    const righe=Object.keys(analisi.punti_mese).filter(k=>!k.startsWith("_")).sort();
                    const totale=analisi.punti_mese["_Totale"];
                    const media=analisi.punti_mese["_Media"];
                    return (
                      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                        <div style={{padding:"6px 14px",background:"#1B2846"}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Punti per mese</span>
                        </div>
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                            <thead>
                              <tr>
                                <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",fontFamily:"'DM Sans',sans-serif",borderBottom:"1px solid #E8E8E4",zIndex:1}}></th>
                                {mesi.map(m=>(<th key={m} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,fontFamily:"'DM Sans',sans-serif",color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{m}</th>))}
                              </tr>
                            </thead>
                            <tbody>
                              {righe.map(stag=>(
                                <tr key={stag}>
                                  <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:1}}>{stag}</td>
                                  {mesi.map(m=>{const val=analisi.punti_mese[stag]?.[m];return(<td key={m} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:m==="TOT"?700:400,color:val>0?"#15803D":val<0?"#DC2626":"#94A3B8",borderBottom:"1px solid #F4F4F0"}}>{val!=null&&val!==0?val.toFixed(2):"—"}</td>);})}
                                </tr>
                              ))}
                              {totale&&(<tr style={{background:"#F8F8F5"}}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:1}}>Totale</td>
                                {mesi.map(m=>{const val=totale[m];return(<td key={m} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:val>0?"#15803D":val<0?"#DC2626":"#94A3B8",borderBottom:"1px solid #E8E8E4"}}>{val!=null&&val!==0?val.toFixed(2):"—"}</td>);})}
                              </tr>)}
                              {media&&(<tr style={{background:"#F8F8F5"}}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,fontStyle:"italic",borderBottom:"1px solid #E8E8E4",zIndex:1}}>Media</td>
                                {mesi.map(m=>{const val=media[m];return(<td key={m} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontStyle:"italic",color:val>0?"#15803D":val<0?"#DC2626":"#94A3B8",borderBottom:"1px solid #E8E8E4"}}>{val!=null&&val!==0?val.toFixed(2):"—"}</td>);})}
                              </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Profitti per sistema */}
                  {analisi.profitti_per_sistema && analisi.profitti_per_sistema._sistemi && analisi.profitti_per_sistema._sistemi.length > 0 && (() => {
                    const pps=analisi.profitti_per_sistema;
                    const sistemiIds=pps._sistemi;
                    const righe=Object.keys(pps).filter(k=>!k.startsWith("_")).sort();
                    const totale=pps["_Totale"];
                    const nomeMap={};
                    sistemi.forEach(s=>{nomeMap[s.id]=s.nome;});
                    return (
                      <div style={{background:"#fff",border:"1px solid #E8E8E4",borderRadius:10,overflow:"hidden",marginBottom:16}}>
                        <div style={{padding:"6px 14px",background:"#1B2846"}}>
                          <span style={{fontSize:10,fontWeight:700,color:"#ffffff",textTransform:"uppercase",letterSpacing:"0.06em"}}>Profitti per sistema</span>
                        </div>
                        <div style={{overflowX:"auto"}}>
                          <table style={{width:"100%",borderCollapse:"collapse",fontSize:10}}>
                            <thead><tr>
                              <th style={{position:"sticky",left:0,background:"#E8EDF5",padding:"5px 8px",textAlign:"left",fontSize:9,fontWeight:700,color:"#1B2846",fontFamily:"'DM Sans',sans-serif",borderBottom:"1px solid #E8E8E4",zIndex:1}}></th>
                              <th style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:9,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4"}}>TOT</th>
                              {sistemiIds.map(sid=>(<th key={sid} style={{background:"#E8EDF5",padding:"5px 6px",textAlign:"right",fontSize:8,fontWeight:700,color:"#1B2846",borderBottom:"1px solid #E8E8E4",whiteSpace:"nowrap"}}>{nomeMap[sid]||sid}</th>))}
                            </tr></thead>
                            <tbody>
                              {righe.map(stag=>(
                                <tr key={stag}>
                                  <td style={{position:"sticky",left:0,background:"#fff",padding:"4px 8px",fontWeight:600,borderBottom:"1px solid #F4F4F0",zIndex:1}}>{stag}</td>
                                  <td style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:(pps[stag]?._TOT||0)>=0?"#15803D":"#DC2626",borderBottom:"1px solid #F4F4F0"}}>{pps[stag]?._TOT?.toFixed(2)}</td>
                                  {sistemiIds.map(sid=>{const val=pps[stag]?.[sid];return(<td key={sid} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,color:val>0?"#15803D":val<0?"#DC2626":"#94A3B8",borderBottom:"1px solid #F4F4F0"}}>{val!=null?val.toFixed(2):"—"}</td>);})}
                                </tr>
                              ))}
                              {totale&&(<tr style={{background:"#F8F8F5"}}>
                                <td style={{position:"sticky",left:0,background:"#F8F8F5",padding:"4px 8px",fontWeight:700,borderBottom:"1px solid #E8E8E4",zIndex:1}}>Totale</td>
                                <td style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:(totale._TOT||0)>=0?"#15803D":"#DC2626",borderBottom:"1px solid #E8E8E4"}}>{totale._TOT?.toFixed(2)}</td>
                                {sistemiIds.map(sid=>{const val=totale[sid];return(<td key={sid} style={{padding:"4px 6px",textAlign:"right",fontFamily:"'DM Sans',sans-serif",fontSize:10,fontWeight:700,color:val>0?"#15803D":val<0?"#DC2626":"#94A3B8",borderBottom:"1px solid #E8E8E4"}}>{val!=null?val.toFixed(2):"—"}</td>);})}
                              </tr>)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Estrai eventi */}
                  <div style={{marginTop:8,display:"flex",justifyContent:"flex-end",gap:8}}>
                    <button onClick={apriDrawer}
                      style={{...btnGhost,fontSize:12,padding:"8px 16px"}}>
                      👁 Visualizza eventi
                    </button>
                    <button onClick={esportaExcel} style={{...btnSave,fontSize:12,padding:"8px 16px"}}>
                      📥 Estrai eventi
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      )}

      {/* ===== DRAWER VISUALIZZA EVENTI ===== */}
      {drawerAperto && (
        <>
          <div onClick={() => setDrawerAperto(false)}
            style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.35)",zIndex:1000}} />
          <div style={{
            position:"fixed",top:0,right:0,bottom:0,
            width:"calc(100vw - 220px)",
            background:"#fff",boxShadow:"-4px 0 24px rgba(0,0,0,0.15)",
            zIndex:1001,display:"flex",flexDirection:"column",
          }}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
              padding:"16px 24px",borderBottom:"1px solid #E2E8F0",background:"#1B2846"}}>
              <div>
                <span style={{fontSize:15,fontWeight:700,color:"#fff"}}>
                  👁 Visualizza eventi — {nomeAnalisi}
                </span>
                {!drawerLoading && (
                  <span style={{marginLeft:12,fontSize:12,color:"#94A3B8"}}>
                    {drawerPartite.length} eventi
                  </span>
                )}
              </div>
              <button onClick={() => setDrawerAperto(false)}
                style={{background:"none",border:"none",color:"#94A3B8",fontSize:20,cursor:"pointer",lineHeight:1}}>
                ✕
              </button>
            </div>
            <div style={{flex:1,overflowY:"auto"}}>
              {drawerLoading ? (
                <div style={{padding:32,textAlign:"center",color:"#64748B"}}>Caricamento eventi…</div>
              ) : drawerPartite.length === 0 ? (
                <div style={{padding:32,textAlign:"center",color:"#64748B"}}>Nessun evento trovato.</div>
              ) : (
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"#E8EDF5",position:"sticky",top:0}}>
                      {["Stagione","Lega","Data","Ora","Casa","Trasferta","Sistema","Mercato","Quota","Punti","Cumulato P/L","Max P/L","Drawdown"].map(h => (
                        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontWeight:600,
                          color:"#1B2846",borderBottom:"2px solid #CBD5E1",whiteSpace:"nowrap"}}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drawerPartite.map((p, i) => (
                      <tr key={i} style={{background:i%2===0?"#fff":"#F8FAFC"}}>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.stagione}</td>
                        <td style={{padding:"7px 12px",color:"#475569"}}>{p.lega}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.data ? (() => { const d = new Date(p.data); return d.toLocaleDateString('it-IT',{weekday:'short',day:'2-digit',month:'2-digit',year:'numeric'}); })() : "—"}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.time_it ? p.time_it.slice(0,5) : "—"}</td>
                        <td style={{padding:"7px 12px",color:"#1E293B",fontWeight:500}}>{p.casa}</td>
                        <td style={{padding:"7px 12px",color:"#1E293B",fontWeight:500}}>{p.trasferta}</td>
                        <td style={{padding:"7px 12px",color:"#475569"}}>{p.sistema}</td>
                        <td style={{padding:"7px 12px",color:"#475569",whiteSpace:"nowrap"}}>{p.mercato}</td>
                        <td style={{padding:"7px 12px",color:"#475569",textAlign:"right"}}>{p.quota ?? "—"}</td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,
                          color:p.punti>0?"#15803D":p.punti<0?"#DC2626":"#475569"}}>
                          {p.punti}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap",
                          color: p.cumulato_pl > 0 ? "#15803D" : p.cumulato_pl < 0 ? "#DC2626" : "#475569"}}>
                          {p.cumulato_pl != null ? p.cumulato_pl.toFixed(2) : "—"}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",color:"#475569",whiteSpace:"nowrap"}}>
                          {p.massimo_storico != null ? p.massimo_storico.toFixed(2) : "—"}
                        </td>
                        <td style={{padding:"7px 12px",textAlign:"right",fontWeight:600,whiteSpace:"nowrap",
                          background: p.drawdown > 0 ? "#FEE2E2" : "transparent",
                          color: p.drawdown > 0 ? "#991B1B" : "#94A3B8"}}>
                          {p.drawdown != null && p.drawdown > 0 ? `-${p.drawdown.toFixed(2)}` : "0"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
